import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Play, Pause, DownloadSimple, CheckCircle, Trash } from "@phosphor-icons/react";
import { fetchTrack, fetchTracks } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import TrackCard from "../components/TrackCard";
import { cacheTrack, removeCachedTrack, isTrackOffline } from "../lib/offline";
import { toast } from "sonner";

export default function TrackDetail() {
  const { id } = useParams();
  const [track, setTrack] = useState(null);
  const [related, setRelated] = useState([]);
  const [offline, setOffline] = useState(false);
  const videoRef = useRef(null);
  const { play, pause, isPlaying, track: current } = usePlayer();

  useEffect(() => {
    fetchTrack(id).then(setTrack).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!track) return;
    setOffline(isTrackOffline(track.id));
    const refresh = () => setOffline(isTrackOffline(track.id));
    window.addEventListener("offline-registry-changed", refresh);
    return () => window.removeEventListener("offline-registry-changed", refresh);
  }, [track]);

  const toggleOffline = async () => {
    if (!track) return;
    if (offline) {
      await removeCachedTrack(track);
      toast.success("Removed from offline library");
    } else {
      await cacheTrack(track);
      toast.success("Saving for offline — you can listen without internet");
    }
  };

  useEffect(() => {
    if (track?.category_slug) {
      fetchTracks({ category: track.category_slug, limit: 10 }).then((items) => setRelated(items.filter((x) => x.id !== track.id).slice(0, 6))).catch(() => {});
    }
  }, [track]);

  if (!track) return <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 text-[#71717A]">Loading…</div>;

  const isAudio = track.type === "audio";
  const isCurrent = current?.id === track.id;
  const playing = isCurrent && isPlaying;

  return (
    <div data-testid="track-detail-page">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <Link to={`/category/${track.category_slug}`} className="overline text-[#B79C7B] hover:text-ivory">← {track.category_slug || "Back"}</Link>

        <div className="grid lg:grid-cols-12 gap-10 mt-8">
          {/* Media */}
          <div className="lg:col-span-8">
            {isAudio ? (
              <div className="aspect-video bg-[#121214] border border-[#2A2A2D] overflow-hidden flex items-end justify-start relative">
                {track.thumbnail && <img src={track.thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                <div className="relative p-10 flex items-end gap-6 w-full">
                  <button
                    onClick={() => (playing ? pause() : play(track))}
                    className="w-20 h-20 rounded-full bg-[#8A2E2E] hover:bg-[#A33B3B] flex items-center justify-center shadow-[0_0_45px_rgba(138,46,46,0.55)]"
                    data-testid="detail-play-button"
                  >
                    {playing ? <Pause size={28} weight="fill" /> : <Play size={28} weight="fill" />}
                  </button>
                  <div>
                    <div className="overline text-[#B79C7B]">Audio · {track.duration || "--:--"}</div>
                    <h1 className="font-serif text-4xl lg:text-5xl text-ivory mt-2">{track.title}</h1>
                    <div className="text-sm text-[#A1A1AA] mt-2">{track.reciter_name}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="aspect-video bg-black border border-[#2A2A2D]">
                <video
                  ref={videoRef}
                  src={track.media_url}
                  poster={track.thumbnail}
                  controls
                  className="w-full h-full"
                  data-testid="detail-video-player"
                />
              </div>
            )}

            <div className="mt-8">
              {!isAudio && <h1 className="font-serif text-4xl text-ivory">{track.title}</h1>}
              {track.description && <p className="text-[#A1A1AA] leading-relaxed mt-4 max-w-3xl">{track.description}</p>}
            </div>
          </div>

          {/* Side meta */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="border border-[#2A2A2D] p-6 bg-[#121214]">
              <div className="overline text-[#B79C7B]">Reciter</div>
              <div className="font-serif text-2xl text-ivory mt-2">{track.reciter_name || "Unknown"}</div>
            </div>

            {isAudio && (
              <button
                onClick={toggleOffline}
                data-testid="offline-toggle-button"
                className={`w-full flex items-center justify-between gap-3 px-5 py-4 border transition-colors ${offline ? "border-[#B79C7B] text-[#B79C7B] bg-[#1A1A1D]" : "border-[#2A2A2D] text-ivory hover:border-[#B79C7B] hover:text-[#B79C7B]"}`}
              >
                <span className="flex items-center gap-3 text-sm tracking-wide">
                  {offline ? <CheckCircle size={18} weight="fill" /> : <DownloadSimple size={18} weight="duotone" />}
                  {offline ? "Available offline" : "Save for offline"}
                </span>
                {offline && <Trash size={14} className="opacity-60" />}
              </button>
            )}

            <div className="border border-[#2A2A2D] p-6 bg-[#121214] space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-[#71717A]">Type</span><span className="text-ivory uppercase">{track.type}</span></div>
              <div className="flex justify-between"><span className="text-[#71717A]">Duration</span><span className="text-ivory font-mono">{track.duration || "--:--"}</span></div>
              <div className="flex justify-between"><span className="text-[#71717A]">Year</span><span className="text-ivory">{track.year || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#71717A]">Plays</span><span className="text-ivory">{track.plays || 0}</span></div>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 border-t border-white/5">
          <h2 className="font-serif text-2xl text-ivory mb-6">More from this section</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {related.map((t) => <TrackCard key={t.id} track={t} />)}
          </div>
        </section>
      )}
    </div>
  );
}
